/**
 * Contactos de la organizacion activa.
 *
 * Los contactos se crean solos cuando el cliente escribe, pero hasta ahora no
 * habia forma de verlos ni de corregirlos desde el panel. Esta pantalla cubre el
 * listado con busqueda y filtros, el alta manual, la ficha con sus campos
 * personalizados, la exportacion a CSV y la gestion de las definiciones de esos
 * campos.
 *
 * Permisos: escribir contactos es cosa de cualquier miembro (la API solo pide
 * pertenecer a la organizacion), pero las definiciones de los campos son
 * configuracion, asi que respetan el mismo `canWrite` que el resto de pantallas
 * de ajustes.
 */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent, ReactElement } from 'react';
import type {
  ContactFieldView,
  ContactListQuery,
  ContactView,
  CreateContactFieldRequest,
  CreateContactRequest,
  UpdateContactRequest,
} from '@shared/api-types.ts';
import { OPT_IN_STATUSES } from '@domain/types.ts';
import type { OptInStatus } from '@domain/types.ts';
import { ApiError, api, errorMessage, exportContactsCsv, isAbortError } from '../api.ts';
import { formatAttributeValue, formatDateTime, formatNumber, formatPhone } from '../format.ts';
import { useDebouncedValue } from '../hooks.ts';
import type { NoticeKind } from '../hooks.ts';
import { CONTACTS_PAGE_SIZE, OPT_IN_LABELS } from '../state.ts';
import { Chip, EmptyState, ErrorBanner, Field, SkeletonRows, Spinner } from './Feedback.tsx';
import { Icon } from './Icon.tsx';

// --- Constantes del contrato -------------------------------------------------

/** Limites espejo de `createSchema` y `fieldSchema` en `src/api/routes/contacts.ts`. */
const WA_ID_MIN = 5;
const PROFILE_NAME_MAX = 120;
const FIELD_LABEL_MAX = 80;
const FIELD_KEY_MAX = 40;
const FIELD_HELP_MAX = 300;
const FIELD_OPTIONS_MAX = 50;
const FIELD_POSITION_MAX = 999;

const CONTACT_FIELD_TYPE_LABELS: Record<ContactFieldView['type'], string> = {
  text: 'Texto corto',
  long_text: 'Texto largo',
  number: 'Numero',
  date: 'Fecha',
  boolean: 'Si o no',
  select: 'Lista de opciones',
  email: 'Correo electronico',
  phone: 'Telefono',
  url: 'Direccion web',
};

/** Orden del desplegable de tipos (los del backend, con los mas usados delante). */
const CONTACT_FIELD_TYPES: readonly ContactFieldView['type'][] = [
  'text',
  'long_text',
  'number',
  'date',
  'boolean',
  'select',
  'email',
  'phone',
  'url',
];

const OPT_IN_TONES: Record<OptInStatus, 'neutral' | 'success' | 'danger'> = {
  unknown: 'neutral',
  opted_in: 'success',
  opted_out: 'danger',
};

/** Campos propios del contacto (fuera de `attributes`) que puede senalar un 400. */
const BASE_CONTACT_FIELDS = ['waId', 'profileName', 'optInStatus'] as const;
type BaseContactField = (typeof BASE_CONTACT_FIELDS)[number];
type BaseContactErrors = Partial<Record<BaseContactField, string>>;

// --- Ayudas de los atributos -------------------------------------------------

/**
 * Valor de un campo personalizado tal y como vive en el formulario: siempre
 * texto, salvo los booleanos, que se manejan como casilla.
 */
type AttributeValue = string | boolean;
type AttributeValues = Record<string, AttributeValue>;

function emptyAttributeValue(field: ContactFieldView): AttributeValue {
  return field.type === 'boolean' ? false : '';
}

function defaultAttributeValues(fields: ContactFieldView[]): AttributeValues {
  const values: AttributeValues = {};
  for (const field of fields) values[field.key] = emptyAttributeValue(field);
  return values;
}

/** Anade los campos que falten (por ejemplo, si las definiciones llegan despues). */
function withDefaults(fields: ContactFieldView[], values: AttributeValues): AttributeValues {
  let next = values;
  for (const field of fields) {
    if (next[field.key] === undefined) next = { ...next, [field.key]: emptyAttributeValue(field) };
  }
  return next;
}

/** Valor guardado (JSON libre) -> valor del formulario. */
function attributeToFormValue(field: ContactFieldView, value: unknown): AttributeValue {
  if (field.type === 'boolean') return value === true || value === 'true';
  if (value === null || value === undefined) return '';
  if (field.type === 'date' && typeof value === 'string') return value.slice(0, 10);
  return typeof value === 'string' ? value : String(value);
}

function initialAttributeValues(
  fields: ContactFieldView[],
  attributes: Record<string, unknown>,
): AttributeValues {
  const values: AttributeValues = {};
  for (const field of fields) {
    values[field.key] = attributeToFormValue(field, attributes[field.key]);
  }
  return values;
}

/**
 * Diferencias de los campos personalizados respecto al contacto original.
 *
 * Solo viaja lo que cambio (el PATCH es minimo) y un campo vaciado se envia como
 * `null` para borrarlo de verdad. Los valores van con su tipo: un campo numerico
 * se guarda como numero, no como texto.
 */
function attributePatch(
  fields: ContactFieldView[],
  original: Record<string, unknown>,
  values: AttributeValues,
): Record<string, unknown> | undefined {
  const patch: Record<string, unknown> = {};
  let changed = false;

  for (const field of fields) {
    const raw = values[field.key] ?? emptyAttributeValue(field);
    const before = attributeToFormValue(field, original[field.key]);

    if (field.type === 'boolean') {
      const next = raw === true;
      if (next !== before) {
        patch[field.key] = next;
        changed = true;
      }
      continue;
    }

    const text = (typeof raw === 'string' ? raw : String(raw)).trim();
    if (text === String(before).trim()) continue;
    changed = true;
    if (text === '') {
      patch[field.key] = null;
      continue;
    }
    patch[field.key] = field.type === 'number' ? Number(text) : text;
  }

  return changed ? patch : undefined;
}

/** Valores de un contacto nuevo: los booleanos viajan siempre, para que queden claros. */
function typedAttributeValues(
  fields: ContactFieldView[],
  values: AttributeValues,
): Record<string, unknown> | undefined {
  const attributes: Record<string, unknown> = {};

  for (const field of fields) {
    const raw = values[field.key] ?? emptyAttributeValue(field);
    if (field.type === 'boolean') {
      attributes[field.key] = raw === true;
      continue;
    }
    const text = (typeof raw === 'string' ? raw : String(raw)).trim();
    if (text === '') continue;
    attributes[field.key] = field.type === 'number' ? Number(text) : text;
  }

  return Object.keys(attributes).length > 0 ? attributes : undefined;
}

/**
 * Validacion del cliente, espejo de `validateAttributes`. El servidor sigue
 * siendo la autoridad: esto solo evita gastar una peticion en fallos obvios.
 */
function validateAttributeValues(
  fields: ContactFieldView[],
  values: AttributeValues,
): Record<string, string> {
  const errors: Record<string, string> = {};

  for (const field of fields) {
    if (field.type === 'boolean') continue;
    const raw = values[field.key];
    const text = typeof raw === 'string' ? raw.trim() : '';

    if (text === '') {
      if (field.isRequired) errors[field.key] = `«${field.label}» es obligatorio.`;
      continue;
    }
    if (field.type === 'number' && !Number.isFinite(Number(text))) {
      errors[field.key] = `«${field.label}» debe ser un numero.`;
    }
    if (field.type === 'select' && !field.options.some((option) => option.value === text)) {
      errors[field.key] = `«${field.label}» debe ser una de las opciones disponibles.`;
    }
  }

  return errors;
}

/**
 * Reparte el `details` de un 400 entre los campos propios del contacto, los
 * campos personalizados (por su clave) y un mensaje general.
 */
function splitInvalidFields(
  details: Record<string, string[]>,
  attributeKeys: ReadonlySet<string>,
): { base: BaseContactErrors; attributes: Record<string, string>; general: string | null } {
  const base: BaseContactErrors = {};
  const attributes: Record<string, string> = {};
  const general: string[] = [];

  for (const [key, messages] of Object.entries(details)) {
    const message = messages.join(' ');
    if ((BASE_CONTACT_FIELDS as readonly string[]).includes(key)) {
      base[`${key as BaseContactField}`] = message;
    } else if (attributeKeys.has(key)) {
      attributes[key] = message;
    } else {
      general.push(message);
    }
  }

  return { base, attributes, general: general.length > 0 ? general.join(' ') : null };
}

function contactName(contact: ContactView): string {
  return contact.profileName?.trim() || `Sin nombre (${formatPhone(contact.waId)})`;
}

function conversationCountText(count: number): string {
  return count === 1 ? '1 conversacion' : `${count} conversaciones`;
}

// --- Controles de los campos personalizados ----------------------------------

/**
 * Un control por tipo de campo: los de texto usan el `type` de HTML que
 * corresponde, los largos un `textarea`, los numericos un input numerico, las
 * fechas un selector de fecha, los booleanos una casilla y las listas el
 * desplegable con las opciones definidas.
 */
function AttributeControl({
  id,
  field,
  value,
  disabled,
  onChange,
}: {
  id: string;
  field: ContactFieldView;
  value: AttributeValue;
  disabled?: boolean;
  onChange: (value: AttributeValue) => void;
}): ReactElement {
  if (field.type === 'boolean') {
    return (
      <label className="checkline" htmlFor={id}>
        <input
          id={id}
          type="checkbox"
          checked={value === true}
          disabled={disabled}
          onChange={(event) => onChange(event.target.checked)}
        />
        <span>{value === true ? 'Si' : 'No'}</span>
      </label>
    );
  }

  const text = typeof value === 'string' ? value : '';

  if (field.type === 'long_text') {
    return (
      <textarea
        id={id}
        className="input input--tall"
        rows={4}
        maxLength={5000}
        value={text}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      />
    );
  }

  if (field.type === 'select') {
    return (
      <select
        id={id}
        className="select"
        value={text}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">Sin valor</option>
        {field.options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    );
  }

  const inputType =
    field.type === 'number'
      ? 'number'
      : field.type === 'date'
        ? 'date'
        : field.type === 'email'
          ? 'email'
          : field.type === 'url'
            ? 'url'
            : field.type === 'phone'
              ? 'tel'
              : 'text';

  return (
    <input
      id={id}
      className="input"
      type={inputType}
      step={field.type === 'number' ? 'any' : undefined}
      maxLength={field.type === 'text' ? 500 : undefined}
      value={text}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

function AttributeField({
  field,
  idPrefix,
  value,
  error,
  disabled,
  onChange,
}: {
  field: ContactFieldView;
  idPrefix: string;
  value: AttributeValue;
  error?: string;
  disabled?: boolean;
  onChange: (value: AttributeValue) => void;
}): ReactElement {
  const id = `${idPrefix}-${field.key}`;
  return (
    <Field
      label={field.label}
      htmlFor={id}
      required={field.isRequired}
      hint={field.helpText ?? undefined}
    >
      <AttributeControl id={id} field={field} value={value} disabled={disabled} onChange={onChange} />
      {error ? <p className="field__error">{error}</p> : null}
    </Field>
  );
}

/** Todos los campos activos de un contacto, con su aviso por campo. */
function AttributeFields({
  fields,
  idPrefix,
  values,
  errors,
  disabled,
  onChange,
}: {
  fields: ContactFieldView[];
  idPrefix: string;
  values: AttributeValues;
  errors: Record<string, string>;
  disabled?: boolean;
  onChange: (key: string, value: AttributeValue) => void;
}): ReactElement {
  const active = useMemo(() => fields.filter((field) => field.isActive), [fields]);

  if (active.length === 0) {
    return (
      <p className="field__hint">
        Esta organizacion todavia no ha definido campos personalizados. Se pueden crear mas abajo,
        en «Campos personalizados».
      </p>
    );
  }

  return (
    <>
      {active.map((field) => (
        <AttributeField
          key={field.key}
          field={field}
          idPrefix={idPrefix}
          value={values[field.key] ?? emptyAttributeValue(field)}
          error={errors[field.key]}
          disabled={disabled}
          onChange={(value) => onChange(field.key, value)}
        />
      ))}
    </>
  );
}

// --- Alta manual de un contacto ----------------------------------------------

function CreateContactForm({
  fields,
  onCreated,
  onCancel,
}: {
  fields: ContactFieldView[];
  onCreated: (contact: ContactView) => void;
  onCancel: () => void;
}): ReactElement {
  const [waId, setWaId] = useState('');
  const [profileName, setProfileName] = useState('');
  const [optIn, setOptIn] = useState<OptInStatus>('unknown');
  const [values, setValues] = useState<AttributeValues>(() => defaultAttributeValues(fields));
  const [baseErrors, setBaseErrors] = useState<BaseContactErrors>({});
  const [attributeErrors, setAttributeErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const activeFields = useMemo(() => fields.filter((field) => field.isActive), [fields]);
  const attributeKeys = useMemo(() => new Set(fields.map((field) => field.key)), [fields]);

  useEffect(() => {
    setValues((prev) => withDefaults(activeFields, prev));
  }, [activeFields]);

  const clearBaseError = useCallback((field: BaseContactField): void => {
    setBaseErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const handleAttributeChange = useCallback((key: string, value: AttributeValue): void => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setAttributeErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>): void => {
      event.preventDefault();
      if (submitting) return;

      const cleanWaId = waId.replace(/\D/g, '');
      const errors: BaseContactErrors = {};
      if (cleanWaId.length < WA_ID_MIN) {
        errors.waId = `El numero debe tener al menos ${WA_ID_MIN} digitos.`;
      }
      if (profileName.trim().length > PROFILE_NAME_MAX) {
        errors.profileName = `El nombre no puede superar los ${PROFILE_NAME_MAX} caracteres.`;
      }
      const attrErrors = validateAttributeValues(activeFields, values);

      if (Object.keys(errors).length > 0 || Object.keys(attrErrors).length > 0) {
        setBaseErrors(errors);
        setAttributeErrors(attrErrors);
        setFormError('Revisa los campos marcados.');
        return;
      }

      const body: CreateContactRequest = { waId: cleanWaId, optInStatus: optIn };
      const name = profileName.trim();
      if (name) body.profileName = name;
      const attributes = typedAttributeValues(activeFields, values);
      if (attributes) body.attributes = attributes;

      setBaseErrors({});
      setAttributeErrors({});
      setFormError(null);
      setSubmitting(true);

      api
        .createContact(body)
        .then((created) => {
          setSubmitting(false);
          onCreated(created);
        })
        .catch((error: unknown) => {
          setSubmitting(false);
          if (error instanceof ApiError) {
            if (error.status === 409) {
              setBaseErrors({ waId: 'Ese numero ya esta dado de alta.' });
              setFormError(
                'Ya tienes un contacto con ese numero. Buscalo en la lista para editarlo en vez de crearlo otra vez.',
              );
              return;
            }
            if (error.status === 400 && error.invalidFields) {
              const parsed = splitInvalidFields(error.invalidFields, attributeKeys);
              setBaseErrors(parsed.base);
              setAttributeErrors(parsed.attributes);
              setFormError(parsed.general ?? 'El servidor ha rechazado algunos campos.');
              return;
            }
          }
          setFormError(errorMessage(error));
        });
    },
    [activeFields, attributeKeys, optIn, profileName, submitting, values, waId],
  );

  return (
    <section className="card">
      <div className="card__row">
        <div>
          <h2 className="card__title">Nuevo contacto</h2>
          <p className="card__hint">
            Para cargar clientes sin esperar a que escriban. El numero se guarda solo con digitos y
            debe incluir el prefijo del pais.
          </p>
        </div>
      </div>

      <fieldset className="fieldset" disabled={submitting}>
        <form onSubmit={handleSubmit} noValidate>
          <div className="grid grid--2">
            <Field
              label="Numero de WhatsApp"
              htmlFor="contact-wa-id"
              required
              hint="Solo digitos, con prefijo del pais. Por ejemplo: 34600000000."
            >
              <input
                id="contact-wa-id"
                className="input input--code"
                inputMode="numeric"
                maxLength={32}
                value={waId}
                placeholder="34600000000"
                onChange={(event) => {
                  setWaId(event.target.value);
                  clearBaseError('waId');
                }}
              />
              {baseErrors.waId ? <p className="field__error">{baseErrors.waId}</p> : null}
            </Field>

            <Field
              label="Nombre"
              htmlFor="contact-profile-name"
              hint={`Como quieres verlo en el panel (maximo ${PROFILE_NAME_MAX} caracteres).`}
            >
              <input
                id="contact-profile-name"
                className="input"
                maxLength={PROFILE_NAME_MAX}
                value={profileName}
                placeholder="Maria Lopez"
                onChange={(event) => {
                  setProfileName(event.target.value);
                  clearBaseError('profileName');
                }}
              />
              {baseErrors.profileName ? (
                <p className="field__error">{baseErrors.profileName}</p>
              ) : null}
            </Field>
          </div>

          <Field
            label="Consentimiento"
            htmlFor="contact-opt-in"
            hint="Si el cliente ha aceptado recibir mensajes. WhatsApp solo deja escribir libremente a quien ha aceptado."
          >
            <select
              id="contact-opt-in"
              className="select"
              value={optIn}
              onChange={(event) => setOptIn(event.target.value as OptInStatus)}
            >
              {OPT_IN_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {OPT_IN_LABELS[status]}
                </option>
              ))}
            </select>
          </Field>

          <AttributeFields
            fields={fields}
            idPrefix="contact-new"
            values={values}
            errors={attributeErrors}
            disabled={submitting}
            onChange={handleAttributeChange}
          />

          {formError ? (
            <div className="banner banner--error" role="alert">
              <Icon name="error" className="banner__icon" />
              <p className="banner__text">{formError}</p>
            </div>
          ) : null}

          <div className="page__actions page__actions--end">
            <button
              type="button"
              className="button button--ghost"
              onClick={onCancel}
              disabled={submitting}
            >
              Cancelar
            </button>
            <button type="submit" className="button button--primary">
              {submitting ? (
                <Spinner label="Guardando…" />
              ) : (
                <>
                  <Icon name="plus" />
                  Crear contacto
                </>
              )}
            </button>
          </div>
        </form>
      </fieldset>
    </section>
  );
}

// --- Ficha y edicion de un contacto ------------------------------------------

function EditContactForm({
  contact: initial,
  fields,
  onSaved,
}: {
  contact: ContactView;
  fields: ContactFieldView[];
  onSaved: (contact: ContactView) => void;
}): ReactElement {
  const [contact, setContact] = useState(initial);
  const [profileName, setProfileName] = useState(initial.profileName ?? '');
  const [optIn, setOptIn] = useState<OptInStatus>(initial.optInStatus);
  const [values, setValues] = useState<AttributeValues>(() =>
    initialAttributeValues(fields, initial.attributes),
  );
  const [baseErrors, setBaseErrors] = useState<BaseContactErrors>({});
  const [attributeErrors, setAttributeErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [formInfo, setFormInfo] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const saveRef = useRef<AbortController | null>(null);

  useEffect(
    () => () => {
      saveRef.current?.abort();
    },
    [],
  );

  const activeFields = useMemo(() => fields.filter((field) => field.isActive), [fields]);
  const attributeKeys = useMemo(() => new Set(fields.map((field) => field.key)), [fields]);

  /*
   * Si mientras la ficha esta abierta se activa un campo nuevo (o llegan tarde
   * las definiciones), se rellena con el valor guardado del contacto y nunca en
   * blanco: guardar no debe borrar un dato que no se ha tocado.
   */
  useEffect(() => {
    setValues((prev) => {
      let next = prev;
      for (const field of activeFields) {
        if (next[field.key] === undefined) {
          next = { ...next, [field.key]: attributeToFormValue(field, contact.attributes[field.key]) };
        }
      }
      return next;
    });
  }, [activeFields, contact.attributes]);

  const handleAttributeChange = useCallback((key: string, value: AttributeValue): void => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setAttributeErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>): void => {
      event.preventDefault();
      if (submitting) return;

      const attrErrors = validateAttributeValues(activeFields, values);
      if (Object.keys(attrErrors).length > 0) {
        setAttributeErrors(attrErrors);
        setFormInfo(null);
        setFormError('Revisa los campos marcados.');
        return;
      }

      // PATCH minimo: solo lo que cambio respecto al contacto original.
      const body: UpdateContactRequest = {};
      const name = profileName.trim();
      if (name !== (contact.profileName ?? '')) body.profileName = name === '' ? null : name;
      if (optIn !== contact.optInStatus) body.optInStatus = optIn;
      const attributes = attributePatch(activeFields, contact.attributes, values);
      if (attributes) body.attributes = attributes;

      if (Object.keys(body).length === 0) {
        setBaseErrors({});
        setAttributeErrors({});
        setFormError(null);
        setFormInfo('No has cambiado nada, asi que no hay nada que guardar.');
        return;
      }

      setBaseErrors({});
      setAttributeErrors({});
      setFormError(null);
      setFormInfo(null);
      setSubmitting(true);

      const controller = new AbortController();
      saveRef.current?.abort();
      saveRef.current = controller;

      api
        .updateContact(contact.id, body, controller.signal)
        .then((updated) => {
          if (controller.signal.aborted) return;
          setSubmitting(false);
          /*
           * El PATCH devuelve el contacto recien guardado pero sin sus
           * conversaciones (`conversationId` y `conversationCount` vuelven a
           * cero), asi que se conserva lo que ya sabiamos para no perder el
           * enlace a la bandeja ni el contador.
           */
          const saved: ContactView = {
            ...updated,
            conversationId: updated.conversationId ?? contact.conversationId,
            conversationCount:
              updated.conversationCount > 0 ? updated.conversationCount : contact.conversationCount,
          };
          // El formulario vuelve a tomar como referencia lo que guardo el servidor.
          setContact(saved);
          setProfileName(saved.profileName ?? '');
          setOptIn(saved.optInStatus);
          setValues(initialAttributeValues(activeFields, saved.attributes));
          onSaved(saved);
        })
        .catch((error: unknown) => {
          if (controller.signal.aborted || isAbortError(error)) return;
          setSubmitting(false);
          if (error instanceof ApiError) {
            if (error.status === 404) {
              setFormError(
                'Ese contacto ya no existe. Cierra la ficha y actualiza la lista.',
              );
              return;
            }
            if (error.status === 400 && error.invalidFields) {
              const parsed = splitInvalidFields(error.invalidFields, attributeKeys);
              setBaseErrors(parsed.base);
              setAttributeErrors(parsed.attributes);
              setFormError(parsed.general ?? 'El servidor ha rechazado algunos campos.');
              return;
            }
          }
          setFormError(errorMessage(error));
        });
    },
    [
      activeFields,
      attributeKeys,
      contact.attributes,
      contact.id,
      contact.optInStatus,
      contact.profileName,
      onSaved,
      optIn,
      profileName,
      submitting,
      values,
    ],
  );

  return (
    <fieldset className="fieldset" disabled={submitting}>
      <form onSubmit={handleSubmit} noValidate>
        <div className="grid grid--2">
          <Field
            label="Nombre"
            htmlFor={`contact-edit-name-${contact.id}`}
            hint={`Vacio borra el nombre guardado (maximo ${PROFILE_NAME_MAX} caracteres).`}
          >
            <input
              id={`contact-edit-name-${contact.id}`}
              className="input"
              maxLength={PROFILE_NAME_MAX}
              value={profileName}
              onChange={(event) => {
                setProfileName(event.target.value);
                setBaseErrors((prev) => {
                  if (!prev.profileName) return prev;
                  const next = { ...prev };
                  delete next.profileName;
                  return next;
                });
              }}
            />
            {baseErrors.profileName ? (
              <p className="field__error">{baseErrors.profileName}</p>
            ) : null}
          </Field>

          <Field label="Consentimiento" htmlFor={`contact-edit-opt-in-${contact.id}`}>
            <select
              id={`contact-edit-opt-in-${contact.id}`}
              className="select"
              value={optIn}
              onChange={(event) => setOptIn(event.target.value as OptInStatus)}
            >
              {OPT_IN_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {OPT_IN_LABELS[status]}
                </option>
              ))}
            </select>
            {baseErrors.optInStatus ? (
              <p className="field__error">{baseErrors.optInStatus}</p>
            ) : null}
          </Field>
        </div>

        <AttributeFields
          fields={fields}
          idPrefix={`contact-edit-${contact.id}`}
          values={values}
          errors={attributeErrors}
          disabled={submitting}
          onChange={handleAttributeChange}
        />

        {formError ? (
          <div className="banner banner--error" role="alert">
            <Icon name="error" className="banner__icon" />
            <p className="banner__text">{formError}</p>
          </div>
        ) : null}

        {formInfo ? (
          <div className="banner banner--info" role="status">
            <Icon name="info" className="banner__icon" />
            <p className="banner__text">{formInfo}</p>
          </div>
        ) : null}

        <div className="page__actions page__actions--end">
          <button type="submit" className="button button--primary">
            {submitting ? (
              <Spinner label="Guardando…" />
            ) : (
              <>
                <Icon name="check" />
                Guardar cambios
              </>
            )}
          </button>
        </div>
      </form>
    </fieldset>
  );
}

/** Fila desplegada: carga la ficha mas reciente y ofrece el formulario. */
function ContactDetailRow({
  contactId,
  fields,
  onSaved,
  onOpenConversation,
  onClose,
}: {
  contactId: string;
  fields: ContactFieldView[];
  onSaved: (contact: ContactView) => void;
  onOpenConversation: (conversationId: string) => void;
  onClose: () => void;
}): ReactElement {
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [contact, setContact] = useState<ContactView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  // Ademas de avisar a la lista, la cabecera de la ficha se queda con lo guardado.
  const handleSaved = useCallback(
    (updated: ContactView): void => {
      setContact(updated);
      onSaved(updated);
    },
    [onSaved],
  );

  useEffect(() => {
    const controller = new AbortController();
    setStatus('loading');
    api
      .getContact(contactId, controller.signal)
      .then((result) => {
        setContact(result);
        setStatus('ready');
      })
      .catch((caught: unknown) => {
        if (isAbortError(caught)) return;
        setError(errorMessage(caught));
        setStatus('error');
      });
    return () => controller.abort();
  }, [contactId, reloadToken]);

  if (status === 'loading') {
    return (
      <div className="diag__loading">
        <Spinner label="Cargando la ficha del contacto…" />
      </div>
    );
  }

  if (status === 'error' || !contact) {
    return (
      <ErrorBanner
        message={error ?? 'No se pudo cargar el contacto.'}
        onRetry={() => setReloadToken((token) => token + 1)}
      />
    );
  }

  const conversationId = contact.conversationId;

  return (
    <div className="diag">
      <div className="diag__head">
        <div>
          <p className="diag__title">{contactName(contact)}</p>
          <p className="diag__hint">
            {formatPhone(contact.waId)} · {conversationCountText(contact.conversationCount)} · alta
            el {formatDateTime(contact.createdAt)}
          </p>
        </div>
        <div className="diag__actions">
          {conversationId ? (
            <button
              type="button"
              className="button button--sm"
              onClick={() => onOpenConversation(conversationId)}
              title="Abrir la conversacion de este contacto en la bandeja"
            >
              <Icon name="inbox" />
              Ir a su conversacion
            </button>
          ) : (
            <span className="editstate__text">Todavia no ha escrito: no hay conversacion.</span>
          )}
          <button type="button" className="button button--ghost button--sm" onClick={onClose}>
            <Icon name="close" />
            Cerrar
          </button>
        </div>
      </div>

      <EditContactForm
        key={`${contact.id}-${contact.lastSeenAt ?? ''}`}
        contact={contact}
        fields={fields}
        onSaved={handleSaved}
      />
    </div>
  );
}

// --- Gestion de los campos personalizados ------------------------------------

/** Una opcion por linea; `valor|Etiqueta` separa lo guardado de lo que se ve. */
function parseFieldOptions(raw: string): { value: string; label: string }[] {
  const options: { value: string; label: string }[] = [];

  for (const line of raw.split('\n')) {
    const clean = line.trim();
    if (clean === '') continue;
    const separator = clean.indexOf('|');
    const value = (separator === -1 ? clean : clean.slice(0, separator)).trim().slice(0, 80);
    const label = (separator === -1 ? clean : clean.slice(separator + 1)).trim().slice(0, 80);
    if (value === '') continue;
    options.push({ value, label: label === '' ? value : label });
    if (options.length >= FIELD_OPTIONS_MAX) break;
  }

  return options;
}

type FieldFormErrors = { label?: string; key?: string; options?: string };

function CreateFieldForm({
  onCreated,
  onCancel,
}: {
  onCreated: (field: ContactFieldView) => void;
  onCancel: () => void;
}): ReactElement {
  const [label, setLabel] = useState('');
  const [key, setKey] = useState('');
  const [type, setType] = useState<ContactFieldView['type']>('text');
  const [isRequired, setIsRequired] = useState(false);
  const [optionsText, setOptionsText] = useState('');
  const [helpText, setHelpText] = useState('');
  const [position, setPosition] = useState('0');
  const [errors, setErrors] = useState<FieldFormErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>): void => {
      event.preventDefault();
      if (submitting) return;

      const cleanLabel = label.trim();
      const cleanKey = key.trim();
      const options = parseFieldOptions(optionsText);
      const clientErrors: FieldFormErrors = {};

      if (cleanLabel === '') {
        clientErrors.label = 'El nombre del campo es obligatorio.';
      } else if (cleanLabel.length > FIELD_LABEL_MAX) {
        clientErrors.label = `El nombre no puede superar los ${FIELD_LABEL_MAX} caracteres.`;
      }
      if (cleanKey !== '' && !/^[a-z][a-z0-9_]*$/.test(cleanKey)) {
        clientErrors.key =
          'La clave debe empezar por letra y contener solo minusculas, numeros y guiones bajos.';
      }
      if (type === 'select' && options.length === 0) {
        clientErrors.options = 'Un campo de lista necesita al menos una opcion.';
      }

      if (Object.keys(clientErrors).length > 0) {
        setErrors(clientErrors);
        setFormError('Revisa los campos marcados.');
        return;
      }

      const parsedPosition = Number(position);
      const body: CreateContactFieldRequest = {
        label: cleanLabel,
        type,
        isRequired,
        position:
          Number.isFinite(parsedPosition) && parsedPosition > 0
            ? Math.min(FIELD_POSITION_MAX, Math.trunc(parsedPosition))
            : 0,
      };
      if (cleanKey) body.key = cleanKey;
      if (type === 'select') body.options = options;
      const cleanHelp = helpText.trim();
      if (cleanHelp) body.helpText = cleanHelp;

      setErrors({});
      setFormError(null);
      setSubmitting(true);

      api
        .createContactField(body)
        .then((created) => {
          setSubmitting(false);
          onCreated(created);
        })
        .catch((error: unknown) => {
          setSubmitting(false);
          if (error instanceof ApiError) {
            if (error.status === 409) {
              setErrors({ key: 'Esa clave ya esta en uso.' });
              setFormError(
                cleanKey
                  ? `Ya existe un campo con la clave «${cleanKey}». Elige otra clave o cambia el nombre.`
                  : 'Ya existe un campo con una clave igual a la que se deriva de ese nombre. Escribe una clave distinta o cambia el nombre.',
              );
              return;
            }
            if (error.status === 400 && error.invalidFields) {
              const messages: FieldFormErrors = {};
              const general: string[] = [];
              for (const [field, list] of Object.entries(error.invalidFields)) {
                const message = list.join(' ');
                if (field === 'label') messages.label = message;
                else if (field === 'key') messages.key = message;
                else if (field === 'options') messages.options = message;
                else general.push(message);
              }
              setErrors(messages);
              setFormError(general.length > 0 ? general.join(' ') : 'El servidor ha rechazado la definicion.');
              return;
            }
          }
          setFormError(errorMessage(error));
        });
    },
    [helpText, isRequired, key, label, onCreated, optionsText, position, submitting, type],
  );

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="grid grid--2">
        <Field
          label="Nombre del campo"
          htmlFor="field-label"
          required
          hint={`Como se vera en el formulario y en la cabecera de la tabla (maximo ${FIELD_LABEL_MAX} caracteres).`}
        >
          <input
            id="field-label"
            className="input"
            maxLength={FIELD_LABEL_MAX}
            value={label}
            placeholder="Ciudad"
            onChange={(event) => {
              setLabel(event.target.value);
              setErrors((prev) => ({ ...prev, label: undefined }));
            }}
          />
          {errors.label ? <p className="field__error">{errors.label}</p> : null}
        </Field>

        <Field
          label="Clave"
          htmlFor="field-key"
          hint={`Opcional: si lo dejas vacio se deriva del nombre. Solo minusculas, numeros y guiones bajos (maximo ${FIELD_KEY_MAX} caracteres).`}
        >
          <input
            id="field-key"
            className="input input--code"
            maxLength={FIELD_KEY_MAX}
            value={key}
            placeholder="ciudad"
            onChange={(event) => {
              setKey(event.target.value);
              setErrors((prev) => ({ ...prev, key: undefined }));
            }}
          />
          {errors.key ? <p className="field__error">{errors.key}</p> : null}
        </Field>

        <Field label="Tipo" htmlFor="field-type" required>
          <select
            id="field-type"
            className="select"
            value={type}
            onChange={(event) => setType(event.target.value as ContactFieldView['type'])}
          >
            {CONTACT_FIELD_TYPES.map((value) => (
              <option key={value} value={value}>
                {CONTACT_FIELD_TYPE_LABELS[value]}
              </option>
            ))}
          </select>
        </Field>

        <Field
          label="Orden"
          htmlFor="field-position"
          hint="Posicion en el formulario y entre las columnas de la tabla (0 va primero)."
        >
          <input
            id="field-position"
            className="input"
            type="number"
            min={0}
            max={FIELD_POSITION_MAX}
            value={position}
            onChange={(event) => setPosition(event.target.value)}
          />
        </Field>
      </div>

      {type === 'select' ? (
        <Field
          label="Opciones"
          htmlFor="field-options"
          required
          hint={`Una por linea, hasta ${FIELD_OPTIONS_MAX}. Escribe «valor|Etiqueta» si quieres separar lo que se guarda de lo que se ve; si no, se usan los dos iguales.`}
        >
          <textarea
            id="field-options"
            className="input input--tall"
            rows={4}
            value={optionsText}
            placeholder="madrid|Madrid"
            onChange={(event) => {
              setOptionsText(event.target.value);
              setErrors((prev) => ({ ...prev, options: undefined }));
            }}
          />
          {errors.options ? <p className="field__error">{errors.options}</p> : null}
        </Field>
      ) : null}

      <div className="grid grid--2">
        <Field
          label="Texto de ayuda"
          htmlFor="field-help"
          hint={`Se muestra bajo el campo en el formulario (maximo ${FIELD_HELP_MAX} caracteres).`}
        >
          <input
            id="field-help"
            className="input"
            maxLength={FIELD_HELP_MAX}
            value={helpText}
            placeholder="Ciudad de residencia del cliente"
            onChange={(event) => setHelpText(event.target.value)}
          />
        </Field>

        <Field label="Obligatorio" hint="El formulario no dejara guardar un contacto sin este dato.">
          <label className="checkline" htmlFor="field-required">
            <input
              id="field-required"
              type="checkbox"
              checked={isRequired}
              onChange={(event) => setIsRequired(event.target.checked)}
            />
            <span>{isRequired ? 'Si, es obligatorio' : 'No es obligatorio'}</span>
          </label>
        </Field>
      </div>

      {formError ? (
        <div className="banner banner--error" role="alert">
          <Icon name="error" className="banner__icon" />
          <p className="banner__text">{formError}</p>
        </div>
      ) : null}

      <div className="page__actions page__actions--end">
        <button
          type="button"
          className="button button--ghost"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancelar
        </button>
        <button type="submit" className="button button--primary">
          {submitting ? (
            <Spinner label="Guardando…" />
          ) : (
            <>
              <Icon name="plus" />
              Crear campo
            </>
          )}
        </button>
      </div>
    </form>
  );
}

/**
 * Listado y gestion de las definiciones de los campos personalizados.
 *
 * Solo los administradores pueden crear, activar o borrar (es configuracion de
 * la organizacion). El aviso sobre el borrado es importante: la API elimina la
 * definicion, nunca los valores ya guardados en los contactos.
 */
function ContactFieldsManager({
  fields,
  canWrite,
  onChanged,
  onNotice,
}: {
  fields: ContactFieldView[];
  canWrite: boolean;
  onChanged: () => void;
  onNotice: (kind: NoticeKind, text: string) => void;
}): ReactElement {
  const [creating, setCreating] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const handleCreated = useCallback(
    (field: ContactFieldView): void => {
      setCreating(false);
      onNotice('success', `Campo «${field.label}» creado (clave «${field.key}»).`);
      onChanged();
    },
    [onChanged, onNotice],
  );

  const handleToggle = useCallback(
    (field: ContactFieldView): void => {
      setBusy(field.id);
      api
        .updateContactField(field.id, { isActive: !field.isActive })
        .then((updated) => {
          onNotice(
            'success',
            `Campo «${updated.label}» ${updated.isActive ? 'activado' : 'desactivado'}.`,
          );
          onChanged();
        })
        .catch((error: unknown) => onNotice('error', errorMessage(error)))
        .finally(() => setBusy(null));
    },
    [onChanged, onNotice],
  );

  const handleDelete = useCallback(
    (field: ContactFieldView): void => {
      setDeleting(field.id);
      api
        .deleteContactField(field.id)
        .then(() => {
          setPendingDelete(null);
          onNotice('success', `Campo «${field.label}» borrado.`);
          onChanged();
        })
        .catch((error: unknown) => onNotice('error', errorMessage(error)))
        .finally(() => setDeleting(null));
    },
    [onChanged, onNotice],
  );

  return (
    <>
      <div className="banner banner--warning" role="status">
        <Icon name="warning" className="banner__icon" />
        <p className="banner__text">
          Borrar un campo no borra los valores ya guardados en los contactos: si vuelves a crear un
          campo con la misma clave, los datos siguen ahi. Desactivarlo lo quita del formulario y de
          la tabla sin perder nada.
        </p>
      </div>

      {!canWrite ? (
        <div className="banner banner--warning" role="status">
          <Icon name="warning" className="banner__icon" />
          <p className="banner__text">
            Solo el propietario y los administradores pueden crear, activar o borrar campos
            personalizados. Puedes consultarlos, pero no cambiarlos.
          </p>
        </div>
      ) : null}

      <div className="card__row">
        <h3 className="card__title">Definiciones</h3>
        {canWrite && !creating ? (
          <button
            type="button"
            className="button button--ghost button--sm"
            onClick={() => setCreating(true)}
          >
            <Icon name="plus" />
            Nuevo campo
          </button>
        ) : null}
      </div>

      {creating ? (
        <CreateFieldForm onCreated={handleCreated} onCancel={() => setCreating(false)} />
      ) : null}

      {fields.length === 0 ? (
        <p className="field__hint">
          No hay campos personalizados. Puedes anadir los que necesites (por ejemplo, ciudad,
          cumpleanos o plan contratado) y apareceran en todos los contactos.
        </p>
      ) : (
        <div className="tablewrap">
          <table className="table">
            <thead>
              <tr>
                <th>Campo</th>
                <th>Clave</th>
                <th>Tipo</th>
                <th>Obligatorio</th>
                <th>Orden</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {fields.map((field) => (
                <tr key={field.id}>
                  <td className="table__strong">{field.label}</td>
                  <td className="table__mono">{field.key}</td>
                  <td>{CONTACT_FIELD_TYPE_LABELS[field.type]}</td>
                  <td>{field.isRequired ? 'Si' : 'No'}</td>
                  <td>{field.position}</td>
                  <td>
                    <Chip tone={field.isActive ? 'success' : 'neutral'}>
                      {field.isActive ? 'Activo' : 'Inactivo'}
                    </Chip>
                  </td>
                  <td>
                    <div className="rowactions">
                      {pendingDelete === field.id ? (
                        <>
                          <button
                            type="button"
                            className="button button--danger button--sm"
                            onClick={() => handleDelete(field)}
                            disabled={deleting === field.id}
                          >
                            {deleting === field.id ? <Spinner label="Borrando…" /> : 'Confirmar borrado'}
                          </button>
                          <button
                            type="button"
                            className="button button--ghost button--sm"
                            onClick={() => setPendingDelete(null)}
                            disabled={deleting === field.id}
                          >
                            Cancelar
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="button button--sm"
                            onClick={() => handleToggle(field)}
                            disabled={!canWrite || busy === field.id}
                            title={
                              canWrite
                                ? 'Quitar o devolver el campo al formulario y a la tabla'
                                : 'Solo los administradores pueden cambiar las definiciones'
                            }
                          >
                            <Icon name={field.isActive ? 'close' : 'check'} />
                            {field.isActive ? 'Desactivar' : 'Activar'}
                          </button>
                          <button
                            type="button"
                            className="button button--ghost button--sm"
                            onClick={() => setPendingDelete(field.id)}
                            disabled={!canWrite}
                            title={
                              canWrite
                                ? 'Borrar la definicion (los valores guardados se conservan)'
                                : 'Solo los administradores pueden cambiar las definiciones'
                            }
                          >
                            <Icon name="trash" />
                            Borrar
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// --- Pantalla ----------------------------------------------------------------

export function ContactsScreen({
  onNotice,
  canWrite,
  onOpenConversation,
}: {
  onNotice: (kind: NoticeKind, text: string) => void;
  /** Las definiciones de campos son configuracion: mismo permiso que los ajustes. */
  canWrite: boolean;
  onOpenConversation: (conversationId: string) => void;
}): ReactElement {
  // --- Filtros ---------------------------------------------------------------
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const [optIn, setOptIn] = useState<OptInStatus | 'all'>('all');
  const [fieldKey, setFieldKey] = useState('');
  const [fieldValue, setFieldValue] = useState('');
  const debouncedFieldValue = useDebouncedValue(fieldValue, 400);

  // --- Listado ---------------------------------------------------------------
  const [items, setItems] = useState<ContactView[]>([]);
  const [listStatus, setListStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [listError, setListError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  // --- Definiciones de campos ------------------------------------------------
  const [fields, setFields] = useState<ContactFieldView[]>([]);
  const [fieldsStatus, setFieldsStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [fieldsError, setFieldsError] = useState<string | null>(null);
  const [fieldsReload, setFieldsReload] = useState(0);

  // --- Formularios y exportacion ---------------------------------------------
  const [createOpen, setCreateOpen] = useState(false);
  const [fieldsOpen, setFieldsOpen] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const exportRef = useRef<AbortController | null>(null);

  const activeFields = useMemo(() => fields.filter((field) => field.isActive), [fields]);
  const fieldFilterTarget = activeFields.find((field) => field.key === fieldKey) ?? null;

  const listQuery = useMemo<ContactListQuery>(
    () => ({
      q: debouncedSearch.trim() === '' ? undefined : debouncedSearch.trim(),
      optInStatus: optIn === 'all' ? undefined : optIn,
      // El filtro por campo viaja como `clave:valor`; el backend compara el
      // valor como texto, aunque el campo guarde numeros o booleanos.
      field:
        fieldKey !== '' && debouncedFieldValue.trim() !== ''
          ? `${fieldKey}:${debouncedFieldValue.trim()}`
          : undefined,
      limit: CONTACTS_PAGE_SIZE,
    }),
    [debouncedFieldValue, debouncedSearch, fieldKey, optIn],
  );

  const hasFilters =
    debouncedSearch.trim() !== '' ||
    optIn !== 'all' ||
    (fieldKey !== '' && debouncedFieldValue.trim() !== '');

  // Definiciones: alimentan las columnas de la tabla y los formularios.
  useEffect(() => {
    const controller = new AbortController();
    setFieldsStatus('loading');
    setFieldsError(null);
    api
      .listContactFields(controller.signal)
      .then((list) => {
        setFields(list);
        setFieldsStatus('ready');
      })
      .catch((error: unknown) => {
        if (isAbortError(error)) return;
        setFieldsError(errorMessage(error));
        setFieldsStatus('error');
      });
    return () => controller.abort();
  }, [fieldsReload]);

  // Primera pagina (y cada vez que cambian los filtros).
  useEffect(() => {
    const controller = new AbortController();
    setListStatus('loading');
    setListError(null);
    api
      .listContacts(listQuery, controller.signal)
      .then((page) => {
        setItems(page.items);
        setNextCursor(page.nextCursor);
        setListStatus('ready');
      })
      .catch((error: unknown) => {
        if (isAbortError(error)) return;
        setListError(errorMessage(error));
        setListStatus('error');
      });
    return () => controller.abort();
  }, [listQuery, reloadToken]);

  useEffect(() => () => exportRef.current?.abort(), []);

  const loadMore = useCallback((): void => {
    if (nextCursor === null || loadingMore) return;
    setLoadingMore(true);
    api
      .listContacts({ ...listQuery, cursor: nextCursor })
      .then((page) => {
        setItems((prev) => {
          const seen = new Set(prev.map((item) => item.id));
          return [...prev, ...page.items.filter((item) => !seen.has(item.id))];
        });
        setNextCursor(page.nextCursor);
      })
      .catch((error: unknown) => onNotice('error', errorMessage(error)))
      .finally(() => setLoadingMore(false));
  }, [listQuery, loadingMore, nextCursor, onNotice]);

  const clearFilters = useCallback((): void => {
    setSearch('');
    setOptIn('all');
    setFieldKey('');
    setFieldValue('');
  }, []);

  const handleCreated = useCallback(
    (created: ContactView): void => {
      setCreateOpen(false);
      setReloadToken((token) => token + 1);
      onNotice('success', `Contacto ${contactName(created)} creado.`);
    },
    [onNotice],
  );

  const handleSaved = useCallback(
    (updated: ContactView): void => {
      setItems((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      onNotice('success', `Contacto ${contactName(updated)} actualizado.`);
    },
    [onNotice],
  );

  // Las definiciones cambian las columnas de la tabla, asi que se recargan las dos.
  const handleFieldsChanged = useCallback((): void => {
    setFieldsReload((token) => token + 1);
    setReloadToken((token) => token + 1);
  }, []);

  const handleExport = useCallback((): void => {
    setExportError(null);
    setExporting(true);
    exportRef.current?.abort();
    const controller = new AbortController();
    exportRef.current = controller;
    exportContactsCsv({ q: listQuery.q }, controller.signal)
      .then(() => {
        if (controller.signal.aborted) return;
        onNotice('success', 'Se ha descargado el fichero CSV de contactos.');
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted || isAbortError(error)) return;
        setExportError(errorMessage(error));
      })
      .finally(() => {
        if (!controller.signal.aborted) setExporting(false);
      });
  }, [listQuery.q, onNotice]);

  const columnCount = 6 + activeFields.length;

  return (
    <div className="page">
      <header className="page__header">
        <div>
          <h1 className="page__title">Contactos</h1>
          <p className="page__subtitle">
            Personas que han escrito a la organizacion o que se han cargado a mano. Los campos
            personalizados permiten guardar datos propios de tu negocio.
          </p>
        </div>
        <div className="page__actions">
          <button
            type="button"
            className="button button--primary"
            onClick={() => setCreateOpen(true)}
            disabled={createOpen}
          >
            <Icon name="plus" />
            Nuevo contacto
          </button>
          <button
            type="button"
            className="button button--ghost"
            onClick={handleExport}
            disabled={exporting}
            title="Descarga un CSV con los contactos que cumplen la busqueda actual"
          >
            {exporting ? <Spinner label="Preparando…" /> : (<><Icon name="copy" />Exportar CSV</>)}
          </button>
          <button
            type="button"
            className="button button--ghost"
            onClick={() => setReloadToken((token) => token + 1)}
          >
            <Icon name="refresh" />
            Actualizar
          </button>
        </div>
      </header>

      {exportError ? (
        <ErrorBanner message={exportError} onRetry={handleExport} retryLabel="Reintentar la descarga" />
      ) : null}

      <section className="card">
        <div className="filters">
          <div className="search">
            <Icon name="search" className="search__icon" />
            <input
              className="input input--search"
              type="search"
              placeholder="Buscar por nombre o numero"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              aria-label="Buscar contactos por nombre o numero"
            />
          </div>

          <div className="filters__row">
            <label className="filters__field">
              <span className="filters__label">Consentimiento</span>
              <select
                className="select"
                value={optIn}
                onChange={(event) => setOptIn(event.target.value as OptInStatus | 'all')}
              >
                <option value="all">Todos</option>
                {OPT_IN_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {OPT_IN_LABELS[status]}
                  </option>
                ))}
              </select>
            </label>

            <label className="filters__field">
              <span className="filters__label">Campo personalizado</span>
              <select
                className="select"
                value={fieldKey}
                onChange={(event) => {
                  setFieldKey(event.target.value);
                  if (event.target.value === '') setFieldValue('');
                }}
                disabled={activeFields.length === 0}
                title={
                  activeFields.length === 0
                    ? 'Todavia no hay campos personalizados activos'
                    : 'Elige por que campo quieres filtrar'
                }
              >
                <option value="">Sin filtro</option>
                {activeFields.map((field) => (
                  <option key={field.key} value={field.key}>
                    {field.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="filters__field">
              <span className="filters__label">Valor del campo</span>
              <input
                className="input"
                value={fieldValue}
                disabled={fieldKey === ''}
                placeholder={
                  fieldKey === ''
                    ? 'Elige antes un campo'
                    : fieldFilterTarget?.type === 'boolean'
                      ? 'true o false'
                      : 'Valor exacto'
                }
                onChange={(event) => setFieldValue(event.target.value)}
              />
            </label>
          </div>

          {fieldFilterTarget ? (
            <p className="field__hint">
              El valor se compara como texto exacto sobre el campo «{fieldFilterTarget.label}».
              {fieldFilterTarget.type === 'boolean' ? ' Escribe true o false.' : ''}
            </p>
          ) : null}

          {hasFilters ? (
            <div className="filters__row filters__row--end">
              <button type="button" className="button button--ghost button--sm" onClick={clearFilters}>
                <Icon name="close" />
                Quitar los filtros
              </button>
            </div>
          ) : null}
        </div>
      </section>

      {createOpen ? (
        <CreateContactForm
          fields={fields}
          onCreated={handleCreated}
          onCancel={() => setCreateOpen(false)}
        />
      ) : null}

      {listStatus === 'loading' ? <SkeletonRows rows={5} /> : null}

      {listStatus === 'error' ? (
        <ErrorBanner
          message={listError ?? 'No se pudieron cargar los contactos.'}
          onRetry={() => setReloadToken((token) => token + 1)}
        />
      ) : null}

      {listStatus === 'ready' && items.length === 0 ? (
        hasFilters ? (
          <EmptyState
            icon="search"
            title="Ningun contacto coincide"
            description="Con la busqueda y los filtros actuales no hay contactos. Prueba a cambiarlos o a quitar el filtro por campo."
            action={
              <button type="button" className="button button--ghost" onClick={clearFilters}>
                <Icon name="close" />
                Quitar los filtros
              </button>
            }
          />
        ) : (
          <EmptyState
            icon="contacts"
            title="Todavia no hay contactos"
            description="Los contactos se crean solos cuando un cliente escribe, pero tambien puedes darlos de alta a mano para tenerlos localizados desde el principio."
            action={
              <button
                type="button"
                className="button button--primary"
                onClick={() => setCreateOpen(true)}
                disabled={createOpen}
              >
                <Icon name="plus" />
                Anadir el primer contacto
              </button>
            }
          />
        )
      ) : null}

      {listStatus === 'ready' && items.length > 0 ? (
        <>
          <div className="tablewrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Numero</th>
                  <th>Consentimiento</th>
                  <th>Ultima vez</th>
                  <th>Conversaciones</th>
                  {activeFields.map((field) => (
                    <th key={field.key} title={`Campo personalizado «${field.key}»`}>
                      {field.label}
                    </th>
                  ))}
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {items.map((contact) => {
                  const isOpen = openId === contact.id;
                  const conversationId = contact.conversationId;
                  return (
                    <Fragment key={contact.id}>
                      <tr>
                        <td className="table__strong table__clip">
                          {contact.profileName?.trim() || 'Sin nombre'}
                        </td>
                        <td className="table__mono">{formatPhone(contact.waId)}</td>
                        <td>
                          <Chip tone={OPT_IN_TONES[contact.optInStatus]}>
                            {OPT_IN_LABELS[contact.optInStatus]}
                          </Chip>
                        </td>
                        <td>{formatDateTime(contact.lastSeenAt)}</td>
                        <td>{formatNumber(contact.conversationCount)}</td>
                        {activeFields.map((field) => (
                          <td className="table__clip" key={field.key}>
                            {formatAttributeValue(contact.attributes[field.key])}
                          </td>
                        ))}
                        <td>
                          <div className="rowactions">
                            <button
                              type="button"
                              className="button button--sm"
                              onClick={() => setOpenId(isOpen ? null : contact.id)}
                              title={
                                isOpen
                                  ? 'Cerrar la ficha de este contacto'
                                  : 'Ver y editar la ficha de este contacto'
                              }
                            >
                              <Icon name={isOpen ? 'close' : 'edit'} />
                              {isOpen ? 'Cerrar' : 'Editar'}
                            </button>
                            {conversationId ? (
                              <button
                                type="button"
                                className="button button--ghost button--sm"
                                onClick={() => onOpenConversation(conversationId)}
                                title="Abrir su conversacion en la bandeja"
                              >
                                <Icon name="inbox" />
                                Conversacion
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                      {isOpen ? (
                        <tr>
                          <td className="table__detail" colSpan={columnCount}>
                            <ContactDetailRow
                              contactId={contact.id}
                              fields={fields}
                              onSaved={handleSaved}
                              onOpenConversation={onOpenConversation}
                              onClose={() => setOpenId(null)}
                            />
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {nextCursor !== null ? (
            <div className="page__actions page__actions--end">
              <button
                type="button"
                className="button button--ghost"
                onClick={loadMore}
                disabled={loadingMore}
              >
                {loadingMore ? 'Cargando…' : 'Cargar mas contactos'}
              </button>
            </div>
          ) : null}
        </>
      ) : null}

      <section className="card">
        <div className="card__row">
          <div>
            <h2 className="card__title">Campos personalizados</h2>
            <p className="card__hint">
              {fieldsStatus === 'error'
                ? 'No se pudieron cargar las definiciones.'
                : `${fields.length} definidos, ${activeFields.length} activos. Se usan como columnas del listado y como campos de la ficha del contacto.`}
            </p>
          </div>
          <button
            type="button"
            className="button button--ghost button--sm"
            onClick={() => setFieldsOpen((open) => !open)}
            aria-expanded={fieldsOpen}
          >
            <Icon name={fieldsOpen ? 'close' : 'edit'} />
            {fieldsOpen ? 'Ocultar' : 'Gestionar campos'}
          </button>
        </div>

        {fieldsStatus === 'error' ? (
          <ErrorBanner
            message={fieldsError ?? 'No se pudieron cargar los campos personalizados.'}
            onRetry={() => setFieldsReload((token) => token + 1)}
          />
        ) : null}

        {fieldsOpen ? (
          <ContactFieldsManager
            fields={fields}
            canWrite={canWrite}
            onChanged={handleFieldsChanged}
            onNotice={onNotice}
          />
        ) : null}
      </section>
    </div>
  );
}
